import AxiosInstance from "service/axiosInstance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "hooks";

const CONFIG_KEY = "pointsConfig";
const MILESTONES_KEY = "rewardMilestones";
const LEADERBOARD_KEY = "leaderboard";

const getConfig = () => AxiosInstance({ method: "GET", url: "api/v1/admin/points-rewards/config" });
const updateConfig = (data) => AxiosInstance({ method: "PUT", url: "api/v1/admin/points-rewards/config", data });

const getMilestones = () => AxiosInstance({ method: "GET", url: "api/v1/admin/points-rewards/milestones" });
const createMilestone = (data) => AxiosInstance({ method: "POST", url: "api/v1/admin/points-rewards/milestones", data });
const updateMilestone = ({ id, ...data }) => AxiosInstance({ method: "PUT", url: `api/v1/admin/points-rewards/milestones/${id}`, data });
const deleteMilestone = (id) => AxiosInstance({ method: "DELETE", url: `api/v1/admin/points-rewards/milestones/${id}` });

const getLeaderboard = (params) => AxiosInstance({ method: "GET", url: "api/v1/admin/points-rewards/leaderboard", params });

export const useGetPointsConfig = () =>
  useQuery({ queryKey: [CONFIG_KEY], queryFn: getConfig, staleTime: 60000 });

export const useUpdatePointsConfig = () => {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation(updateConfig, {
    onSuccess: () => {
      qc.invalidateQueries([CONFIG_KEY]);
      qc.invalidateQueries([LEADERBOARD_KEY]);
      toast.toastSuccess("Configuration updated successfully");
    },
    onError: (e) => toast.toastError(e?.message || "Failed to update configuration"),
  });
};

export const useGetMilestones = () =>
  useQuery({ queryKey: [MILESTONES_KEY], queryFn: getMilestones, staleTime: 60000 });

export const useCreateMilestone = () => {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation(createMilestone, {
    onSuccess: () => {
      qc.invalidateQueries([MILESTONES_KEY]);
      qc.invalidateQueries([LEADERBOARD_KEY]);
      toast.toastSuccess("Reward milestone created successfully");
    },
    onError: (e) => toast.toastError(e?.message || "Failed to create milestone"),
  });
};

export const useUpdateMilestone = () => {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation(updateMilestone, {
    onSuccess: () => {
      qc.invalidateQueries([MILESTONES_KEY]);
      qc.invalidateQueries([LEADERBOARD_KEY]);
      toast.toastSuccess("Reward milestone updated successfully");
    },
    onError: (e) => toast.toastError(e?.message || "Failed to update milestone"),
  });
};

export const useDeleteMilestone = () => {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation(deleteMilestone, {
    onSuccess: () => {
      qc.invalidateQueries([MILESTONES_KEY]);
      qc.invalidateQueries([LEADERBOARD_KEY]);
      toast.toastSuccess("Reward milestone deleted successfully");
    },
    onError: (e) => toast.toastError(e?.message || "Failed to delete milestone"),
  });
};

export const useGetLeaderboard = (params) =>
  useQuery({
    queryKey: [LEADERBOARD_KEY, params],
    queryFn: () => getLeaderboard(params),
    keepPreviousData: true,
    staleTime: 10000,
  });
